export const API_BASE = process.env.VUE_APP_API_BASE || "http://localhost:5165";

const STORAGE_KEY = "english_study_lab_state_v1";
const MAX_RECENT_ITEMS = 12;

function looksLikeHtml(value) {
  return typeof value === "string" && /<\s*(!doctype|html|head|body)\b/i.test(value);
}

export function ensureApiObject(response, label = "API") {
  const data = response?.data;
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return data;
  }

  if (looksLikeHtml(data)) {
    throw new Error(`${label} returned HTML instead of JSON.`);
  }

  throw new Error(`${label} returned an unexpected payload.`);
}

export function ensureApiArray(response, label = "API") {
  const data = response?.data;
  if (Array.isArray(data)) {
    return data;
  }

  if (looksLikeHtml(data)) {
    throw new Error(`${label} returned HTML instead of JSON.`);
  }

  throw new Error(`${label} returned an unexpected payload.`);
}

function emptyState() {
  return {
    words: {},
    roots: {},
    recentWordIds: [],
    recentRootIds: [],
    daily: {},
  };
}

function todayKey() {
  return new Date().toISOString().slice(0, 10);
}

function readState() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return emptyState();
    }

    const parsed = JSON.parse(raw);
    return {
      ...emptyState(),
      ...parsed,
      words: parsed?.words || {},
      roots: parsed?.roots || {},
      recentWordIds: Array.isArray(parsed?.recentWordIds) ? parsed.recentWordIds : [],
      recentRootIds: Array.isArray(parsed?.recentRootIds) ? parsed.recentRootIds : [],
      daily: parsed?.daily || {},
    };
  } catch (error) {
    console.error("Failed to read english study state:", error);
    return emptyState();
  }
}

function writeState(state) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
}

function pushRecent(list, id) {
  const next = [id, ...list.filter((item) => item !== id)];
  return next.slice(0, MAX_RECENT_ITEMS);
}

function markDaily(state, category, id) {
  const key = todayKey();
  const bucket = state.daily[key] || { words: [], roots: [] };
  if (category === "word") {
    bucket.words = [...new Set([id, ...bucket.words])];
  } else {
    bucket.roots = [...new Set([id, ...bucket.roots])];
  }

  state.daily = {
    [key]: bucket,
  };
}

function wordSnapshot(word) {
  return {
    wordId: word.word_id,
    wordText: word.word_text,
    rootId: word.root_id,
    pronunciationText: getPronunciation(word),
    meaning: getWordMeaning(word, "en"),
    updatedAt: word.updated_at || null,
  };
}

function rootSnapshot(root) {
  return {
    rootId: root.root_id,
    rootText: root.root_text,
    kind: root.kind,
    meaning: getRootMeaning(root, "en"),
  };
}

export function recordWordVisit(word) {
  if (!word?.word_id) {
    return;
  }

  const state = readState();
  const current = state.words[word.word_id] || {};
  state.words[word.word_id] = {
    ...current,
    ...wordSnapshot(word),
    viewed: true,
    favorite: !!current.favorite,
    mastered: !!current.mastered,
    viewCount: Number(current.viewCount || 0) + 1,
    lastViewedAt: new Date().toISOString(),
  };
  state.recentWordIds = pushRecent(state.recentWordIds, word.word_id);
  markDaily(state, "word", word.word_id);
  writeState(state);
}

export function recordRootVisit(root) {
  if (!root?.root_id) {
    return;
  }

  const state = readState();
  const current = state.roots[root.root_id] || {};
  state.roots[root.root_id] = {
    ...current,
    ...rootSnapshot(root),
    viewed: true,
    viewCount: Number(current.viewCount || 0) + 1,
    lastViewedAt: new Date().toISOString(),
  };
  state.recentRootIds = pushRecent(state.recentRootIds, root.root_id);
  markDaily(state, "root", root.root_id);
  writeState(state);
}

export function toggleWordFlag(word, flag) {
  if (!word?.word_id || !["favorite", "mastered"].includes(flag)) {
    return false;
  }

  const state = readState();
  const current = state.words[word.word_id] || wordSnapshot(word);
  const nextValue = !current[flag];
  state.words[word.word_id] = {
    ...current,
    ...wordSnapshot(word),
    viewed: true,
    favorite: flag === "favorite" ? nextValue : !!current.favorite,
    mastered: flag === "mastered" ? nextValue : !!current.mastered,
    viewCount: Number(current.viewCount || 0),
    lastViewedAt: current.lastViewedAt || new Date().toISOString(),
  };
  state.recentWordIds = pushRecent(state.recentWordIds, word.word_id);
  writeState(state);
  return nextValue;
}

export function getWordStudyState(wordId) {
  const state = readState();
  const current = state.words[wordId] || {};
  return {
    viewed: !!current.viewed,
    favorite: !!current.favorite,
    mastered: !!current.mastered,
    viewCount: Number(current.viewCount || 0),
    lastViewedAt: current.lastViewedAt || "",
  };
}

export function getStudyDashboard() {
  const state = readState();
  const today = state.daily[todayKey()] || { words: [], roots: [] };
  const wordEntries = Object.values(state.words);
  const rootEntries = Object.values(state.roots);

  return {
    recentWords: state.recentWordIds.map((id) => state.words[id]).filter(Boolean),
    recentRoots: state.recentRootIds.map((id) => state.roots[id]).filter(Boolean),
    todayWordCount: today.words.length,
    todayRootCount: today.roots.length,
    viewedWordCount: wordEntries.filter((item) => item.viewed).length,
    favoriteWordCount: wordEntries.filter((item) => item.favorite).length,
    masteredWordCount: wordEntries.filter((item) => item.mastered).length,
    viewedRootCount: rootEntries.filter((item) => item.viewed).length,
  };
}

export function pickLocalizedText(value, lang, fallback = "-") {
  if (!value) {
    return fallback;
  }

  if (typeof value === "string") {
    return value.trim() || fallback;
  }

  const text = value[lang];
  if (typeof text === "string" && text.trim()) {
    return text.trim();
  }

  return fallback;
}

export function getPronunciation(word) {
  const candidates = [
    word?.phonetic,
    word?.ipa,
    word?.pronunciation,
    word?.pronunciationText,
    word?.pronunciationtext,
    word?.pronunciation_ipa,
    word?.word_phonetic,
  ];

  for (const raw of candidates) {
    const text = String(raw || "").trim();
    if (text) {
      return text;
    }
  }

  return "-";
}

export function getWordMeaning(word, lang = "ja") {
  const items = getMeaningItems(word);
  if (items.length === 0) {
    return "-";
  }

  const first = items[0];
  if (lang === "zh") {
    return first.meaning_ch || first.meaning_en || first.meaning_jp || "-";
  }

  if (lang === "en") {
    return first.meaning_en || first.meaning_jp || first.meaning_ch || "-";
  }

  return first.meaning_jp || first.meaning_en || first.meaning_ch || "-";
}

export function getRootMeaning(root, lang = "ja") {
  if (!root) {
    return "-";
  }

  if (lang === "zh") {
    return root.meaning_ch || root.meaning_en || root.meaning_ja || "-";
  }

  if (lang === "en") {
    return root.meaning_en || root.meaning_ja || root.meaning_ch || "-";
  }

  return root.meaning_ja || root.meaning_en || root.meaning_ch || "-";
}

export function getMeaningItems(word) {
  const items = word?.meaning?.items;
  if (Array.isArray(items)) {
    return items;
  }

  if (items && typeof items === "object") {
    return [items];
  }

  return [];
}

const TYPE_LABELS = {
  ja: { p: "Prefix", r: "Root", s: "Suffix", c: "Connector", e: "Elision" },
  zh: { p: "前缀", r: "词根", s: "后缀", c: "连接成分", e: "省略变化" },
  en: { p: "Prefix", r: "Root", s: "Suffix", c: "Connector", e: "Elision" },
};

function buildCandidates(type, occurrence) {
  const aliases = {
    p: ["prefix", "p_prefix"],
    r: ["root", "root1", "root2"],
    s: ["suffix", "s_suffix", "ss_suffix"],
    c: ["connector"],
    e: ["elision"],
  };

  const candidates = [`${type}${occurrence}`];
  if (occurrence === 0) {
    candidates.push(type);
  }

  if (type === "r" && occurrence > 0) {
    candidates.push(`r${occurrence}`);
  }

  return [...new Set([...candidates, ...(aliases[type] || [])])];
}

function getPartValue(obj, type, occurrence) {
  const candidates = buildCandidates(type, occurrence);
  for (const key of candidates) {
    const value = obj?.[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value);
    }
  }

  return "-";
}

function splitMeaningText(raw) {
  const [en = "", ja = "", zh = ""] = String(raw || "").split("/");
  return { en: en || "-", ja: ja || en || zh || "-", zh: zh || en || ja || "-" };
}

export function getWordChar(word, lang = "ja") {
  const meaning = splitMeaningText(word?.structure_mean?.char || "");
  if (lang === "zh") {
    return meaning.zh;
  }

  if (lang === "en") {
    return meaning.en;
  }

  return meaning.ja;
}

export function getStructureParts(word, lang = "ja") {
  const chars = String(word?.structure_type || "")
    .split("")
    .filter((token) => TYPE_LABELS.en[token]);
  const counters = { p: 0, r: 0, s: 0, c: 0, e: 0 };
  const totals = { p: 0, r: 0, s: 0, c: 0, e: 0 };

  chars.forEach((token) => {
    totals[token] += 1;
  });

  return chars
    .map((type, index) => {
      const occurrence = counters[type];
      counters[type] += 1;

      const meaning = splitMeaningText(getPartValue(word?.structure_mean || {}, type, occurrence));
      const labelBase = TYPE_LABELS[lang]?.[type] || TYPE_LABELS.en[type];
      const label = totals[type] > 1 ? `${labelBase} ${occurrence + 1}` : labelBase;

      return {
        index,
        type,
        label,
        text: getPartValue(word?.structure || {}, type, occurrence),
        meaningEn: meaning.en,
        meaningJa: meaning.ja,
        meaningZh: meaning.zh,
      };
    })
    .filter((part) => part.text !== "-");
}

export function formatDate(value) {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);
}

export function speakEnglish(text) {
  const value = String(text || "").trim();
  if (!value || typeof window === "undefined" || !("speechSynthesis" in window)) {
    return;
  }

  window.speechSynthesis.cancel();
  const utterance = new SpeechSynthesisUtterance(value);
  utterance.lang = "en-US";
  utterance.rate = 0.72;
  utterance.pitch = 1;

  const voices = window.speechSynthesis.getVoices();
  const voice = voices.find((item) => String(item.lang || "").toLowerCase().startsWith("en"));
  if (voice) {
    utterance.voice = voice;
  }

  window.speechSynthesis.speak(utterance);
}
